import React, { useState, useMemo } from 'react';
import { Mail, Plus, Send, ChevronRight, Search, X } from 'lucide-react';
import { safeAsync, Badge, emptyMsg, btnPrimary, btnSm, cardWrap, neuShadow, neuShadowSm, inp } from './crmShared';

const STATUS_ORDER = ['draft', 'scheduled', 'active', 'paused', 'completed', 'cancelled'];
const STATUS_COLORS = {
  draft: '#F26522',
  scheduled: '#2E8B98',
  active: '#62BB46',
  paused: '#256F7A',
  completed: '#4FA038',
  cancelled: '#6C757D'
};

export default function CampaignsTab({ campaigns, contacts, canCreate, loadExtended, onContactClick }) {
  const [expandedGroups, setExpandedGroups] = useState(new Set(['active', 'scheduled'])); // Start with common statuses expanded
  const [searchQuery, setSearchQuery] = useState('');

  // Determine if search is active
  const isSearching = searchQuery.trim().length > 0;

  // Filter campaigns by search query
  const filteredCampaigns = useMemo(() => {
    if (!isSearching) return campaigns;
    const q = searchQuery.toLowerCase();
    return campaigns.filter(c =>
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.subject && c.subject.toLowerCase().includes(q))
    );
  }, [campaigns, searchQuery, isSearching]);

  // Group filtered campaigns by status
  const groupedCampaigns = useMemo(() => {
    const grouped = {};
    STATUS_ORDER.forEach(status => {
      grouped[status] = filteredCampaigns.filter(c => c.status === status);
    });
    return grouped;
  }, [filteredCampaigns]);

  // Auto-expand all groups when searching
  const effectiveExpandedGroups = useMemo(() => {
    if (isSearching) {
      return new Set(STATUS_ORDER);
    }
    return expandedGroups;
  }, [expandedGroups, isSearching]);

  const toggleGroup = (status) => {
    const newExpanded = new Set(effectiveExpandedGroups);
    if (newExpanded.has(status)) {
      newExpanded.delete(status);
    } else {
      newExpanded.add(status);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(STATUS_ORDER));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div>
      {/* Header: Title + New Campaign */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={18} color="#4FA038" /> Email Campaigns
        </h3>
        {canCreate && (
          <button onClick={safeAsync(async () => {
            const name = prompt('Campaign name:');
            if (!name) return;
            const subject = prompt('Email subject (use {{first_name}} for merge):');
            if (!subject) return;
            const body = prompt('Email body HTML (use {{first_name}}, {{last_name}} for merge):');
            if (!body) return;
            const res = await window.zignagenixAPI.createCampaign({ name, subject, body, campaign_type: 'blast' });
            if (res.success) {
              const withEmail = contacts.filter(c => c.email);
              if (withEmail.length > 0) {
                await window.zignagenixAPI.addCampaignRecipients({ campaignId: res.campaignId, contactIds: withEmail.map(c => c.id) });
              }
              alert(`Campaign created with ${withEmail.length} recipients!`);
              loadExtended();
            }
          })} style={btnPrimary}><Plus size={13} /> New Campaign</button>
        )}
      </div>

      {/* Campaign Stats Summary */}
      {campaigns.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Total', value: campaigns.length, color: '#2E8B98' },
            { label: 'Active', value: campaigns.filter(c => c.status === 'active').length, color: '#62BB46' },
            { label: 'Completed', value: campaigns.filter(c => c.status === 'completed').length, color: '#4FA038' },
            { label: 'Draft', value: campaigns.filter(c => c.status === 'draft').length, color: '#F26522' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#F8F9FA', borderRadius: 12, boxShadow: neuShadow, padding: '12px 20px', textAlign: 'center', minWidth: 90 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: '#6C757D' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search Bar */}
      {campaigns.length > 0 && (
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, color: '#ADB5BD' }} />
            <input
              type="text"
              placeholder="Search by campaign name or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                ...inp,
                paddingLeft: 36,
                paddingRight: searchQuery ? 36 : 12,
                width: '100%'
              }}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                style={{
                  position: 'absolute',
                  right: 10,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: '#ADB5BD',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Expand/Collapse All Buttons */}
      {campaigns.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            onClick={expandAll}
            style={{
              ...btnSm,
              background: '#F8F9FA',
              border: '1px solid #DDE1E6',
              color: '#495057'
            }}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            style={{
              ...btnSm,
              background: '#F8F9FA',
              border: '1px solid #DDE1E6',
              color: '#495057'
            }}
          >
            Collapse All
          </button>
        </div>
      )}

      {/* Campaign List with Accordion */}
      <div style={cardWrap}>
        {campaigns.length === 0 ? (
          <div style={emptyMsg}>No email campaigns yet. Create your first campaign to start sending.</div>
        ) : filteredCampaigns.length === 0 ? (
          <div style={emptyMsg}>No campaigns match your search. Try a different query.</div>
        ) : (
          STATUS_ORDER.map(status => {
            const groupCampaigns = groupedCampaigns[status];
            const isExpanded = effectiveExpandedGroups.has(status);
            const hasItems = groupCampaigns.length > 0;

            if (!hasItems) return null;

            return (
              <div key={status} style={{ borderBottom: '1px solid #E9ECEF' }}>
                {/* Group Header - Accordion Toggle */}
                <button
                  onClick={() => toggleGroup(status)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: '#F8F9FA',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    transition: 'background-color 0.2s',
                    ':hover': { background: '#E9ECEF' }
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#E9ECEF'}
                  onMouseLeave={(e) => e.target.style.background = '#F8F9FA'}
                >
                  <ChevronRight
                    size={18}
                    style={{
                      color: STATUS_COLORS[status],
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      flexShrink: 0
                    }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#212529', textTransform: 'capitalize' }}>
                    {status}
                  </span>
                  <Badge text={`${groupCampaigns.length}`} color={STATUS_COLORS[status]} />
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6C757D' }}>
                    {isExpanded ? 'Collapse' : 'Expand'}
                  </span>
                </button>

                {/* Group Content - Accordion Body */}
                <div
                  style={{
                    maxHeight: isExpanded ? '10000px' : '0px',
                    overflow: 'hidden',
                    transition: 'maxHeight 0.3s ease',
                    background: '#FFFFFF'
                  }}
                >
                  {groupCampaigns.map((c, i) => {
                    const stats = typeof c.stats === 'object' ? c.stats : {};
                    return (
                      <div
                        key={c.id}
                        style={{
                          padding: '14px 16px',
                          borderTop: i === 0 ? 'none' : '1px solid #E9ECEF',
                          background: i % 2 === 0 ? '#FFFFFF' : '#F8F9FA'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>{c.name}</span>
                          <Badge text={c.campaign_type} color="#2E8B98" />
                          <span style={{ fontSize: 10, color: '#ADB5BD', marginLeft: 'auto' }}>
                            {c.recipient_count || 0} recipients
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 6 }}>
                          Subject: {c.subject}
                        </div>
                        {/* Stats bar */}
                        <div style={{ display: 'flex', gap: 12, fontSize: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                          <span style={{ color: '#2E8B98' }}>Sent: {stats.sent || 0}</span>
                          <span style={{ color: '#62BB46' }}>Delivered: {stats.delivered || 0}</span>
                          <span style={{ color: '#256F7A' }}>Opened: {stats.opened || 0}</span>
                          <span style={{ color: '#F26522' }}>Clicked: {stats.clicked || 0}</span>
                          <span style={{ color: '#DC3545' }}>Bounced: {stats.bounced || 0}</span>
                        </div>
                        {/* Actions */}
                        {c.status === 'draft' && canCreate && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              onClick={safeAsync(async () => {
                                if (!window.confirm(`Send campaign "${c.name}" to ${c.recipient_count} recipients?`))
                                  return;
                                const res = await window.zignagenixAPI.sendCampaign(c.id);
                                alert(
                                  res.success
                                    ? `Sent: ${res.sent}, Failed: ${res.failed}`
                                    : `Error: ${res.error}`
                                );
                                loadExtended();
                              })}
                              style={btnPrimary}
                            >
                              <Send size={12} /> Send Now
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
